import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useCreateCustomer, useUpdateCustomer, type CreateCustomerInput, type CustomerType, type Customer } from "@/hooks/useCustomers";
import { useCustomerDuplicates, findPotentialMatches } from "@/hooks/useCustomerDuplicates";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const CUSTOMER_TYPES: { value: CustomerType; label: string }[] = [
  { value: "general_contractor", label: "General Contractor" },
  { value: "property_manager", label: "Property Manager" },
  { value: "owner", label: "Owner" },
  { value: "insurance_company", label: "Insurance Company" },
  { value: "other", label: "Other" },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName?: string;
  onCreated?: (customer: { id: string; name: string }) => void;
  editCustomer?: Customer | null;
}

export const NewCustomerDialog = ({ open, onOpenChange, initialName, onCreated, editCustomer }: Props) => {
  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();
  const isEdit = !!editCustomer;

  const [name, setName] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [customerType, setCustomerType] = useState<CustomerType>("general_contractor");
  const [billingAddress, setBillingAddress] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingState, setBillingState] = useState("");
  const [billingZip, setBillingZip] = useState("");
  const [jobSameAsBilling, setJobSameAsBilling] = useState(true);
  const [jobAddress, setJobAddress] = useState("");
  const [jobCity, setJobCity] = useState("");
  const [jobState, setJobState] = useState("");
  const [jobZip, setJobZip] = useState("");
  const [notes, setNotes] = useState("");
  const [dismissedWarning, setDismissedWarning] = useState(false);

  // Duplicate detection
  const { allCustomers } = useCustomerDuplicates();
  const potentialMatches = useMemo(
    () => findPotentialMatches(allCustomers, name, email, phone, editCustomer?.id),
    [allCustomers, name, email, phone, editCustomer?.id]
  );

  useEffect(() => {
    if (open) {
      setDismissedWarning(false);
      if (editCustomer) {
        setName(editCustomer.name);
        setContactName(editCustomer.contact_name || "");
        setEmail(editCustomer.email || "");
        setPhone(editCustomer.phone || "");
        setCustomerType((editCustomer.customer_type as CustomerType) || "general_contractor");
        setBillingAddress(editCustomer.billing_address || "");
        setBillingCity(editCustomer.billing_city || "");
        setBillingState(editCustomer.billing_state || "");
        setBillingZip(editCustomer.billing_zip || "");
        setNotes(editCustomer.notes || "");
        const hasSeparateJob = editCustomer.job_address || editCustomer.job_city;
        setJobSameAsBilling(!hasSeparateJob);
        setJobAddress(editCustomer.job_address || "");
        setJobCity(editCustomer.job_city || "");
        setJobState(editCustomer.job_state || "");
        setJobZip(editCustomer.job_zip || "");
      } else {
        setName(initialName || "");
        setContactName("");
        setEmail("");
        setPhone("");
        setCustomerType("general_contractor");
        setBillingAddress("");
        setBillingCity("");
        setBillingState("");
        setBillingZip("");
        setJobSameAsBilling(true);
        setJobAddress("");
        setJobCity("");
        setJobState("");
        setJobZip("");
        setNotes("");
      }
    }
  }, [open, initialName, editCustomer]);

  const handleSave = async () => {
    if (!name.trim()) return toast.error("Company name is required");
    if (!contactName.trim()) return toast.error("Primary contact name is required");
    if (!email.trim()) return toast.error("Email is required");
    if (!phone.trim()) return toast.error("Phone is required");

    const input: CreateCustomerInput = {
      name: name.trim(),
      contact_name: contactName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      customer_type: customerType,
      billing_address: billingAddress.trim() || undefined,
      billing_city: billingCity.trim() || undefined,
      billing_state: billingState.trim() || undefined,
      billing_zip: billingZip.trim() || undefined,
      job_address: jobSameAsBilling ? billingAddress.trim() || undefined : jobAddress.trim() || undefined,
      job_city: jobSameAsBilling ? billingCity.trim() || undefined : jobCity.trim() || undefined,
      job_state: jobSameAsBilling ? billingState.trim() || undefined : jobState.trim() || undefined,
      job_zip: jobSameAsBilling ? billingZip.trim() || undefined : jobZip.trim() || undefined,
      notes: notes.trim() || undefined,
    };

    try {
      if (isEdit) {
        await updateCustomer.mutateAsync({ id: editCustomer.id, ...input });
        toast.success("Customer updated");
      } else {
        const result = await createCustomer.mutateAsync(input);
        toast.success("Customer created — will sync to QuickBooks on next sync");
        onCreated?.(result);
      }
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || "Failed to save customer");
    }
  };

  const isPending = createCustomer.isPending || updateCustomer.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Customer" : "New Customer"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Update customer information" : "Add a new customer to your directory"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Required Fields */}
          <div className="space-y-3">
            <div>
              <Label htmlFor="cust-name">Company Name *</Label>
              <Input id="cust-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Construction" />
            </div>
            <div>
              <Label htmlFor="cust-contact">Primary Contact Name *</Label>
              <Input id="cust-contact" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="John Smith" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cust-email">Email *</Label>
                <Input id="cust-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="john@acme.com" />
              </div>
              <div>
                <Label htmlFor="cust-phone">Phone *</Label>
                <Input id="cust-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" />
              </div>
            </div>
          </div>

          {/* Duplicate Warning */}
          {potentialMatches.length > 0 && !dismissedWarning && !isEdit && (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    Potential duplicate{potentialMatches.length > 1 ? "s" : ""} found
                  </p>
                  <div className="mt-1 space-y-1">
                    {potentialMatches.slice(0, 3).map((m) => (
                      <div key={m.customer.id} className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-2">
                        <span className="font-medium">{m.customer.name}</span>
                        {m.customer.customer_number != null && (
                          <span className="font-mono text-amber-600">#{m.customer.customer_number}</span>
                        )}
                        <span>— {m.reasons.join(", ")}</span>
                      </div>
                    ))}
                    {potentialMatches.length > 3 && (
                      <p className="text-xs text-amber-600">…and {potentialMatches.length - 3} more</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 ml-6">
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setDismissedWarning(true)}>
                  Create Anyway
                </Button>
              </div>
            </div>
          )}

          {/* Customer Type */}
          <div>
            <Label>Customer Type</Label>
            <Select value={customerType} onValueChange={(v) => setCustomerType(v as CustomerType)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CUSTOMER_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Billing Address */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Billing Address</Label>
            <Input value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)} placeholder="Street" />
            <div className="grid grid-cols-3 gap-2">
              <Input value={billingCity} onChange={(e) => setBillingCity(e.target.value)} placeholder="City" />
              <Input value={billingState} onChange={(e) => setBillingState(e.target.value)} placeholder="State" />
              <Input value={billingZip} onChange={(e) => setBillingZip(e.target.value)} placeholder="Zip" />
            </div>
          </div>

          {/* Job Address */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox id="same-billing" checked={jobSameAsBilling} onCheckedChange={(v) => setJobSameAsBilling(!!v)} />
              <Label htmlFor="same-billing" className="text-sm">Job address same as billing</Label>
            </div>
            {!jobSameAsBilling && (
              <>
                <Input value={jobAddress} onChange={(e) => setJobAddress(e.target.value)} placeholder="Street" />
                <div className="grid grid-cols-3 gap-2">
                  <Input value={jobCity} onChange={(e) => setJobCity(e.target.value)} placeholder="City" />
                  <Input value={jobState} onChange={(e) => setJobState(e.target.value)} placeholder="State" />
                  <Input value={jobZip} onChange={(e) => setJobZip(e.target.value)} placeholder="Zip" />
                </div>
              </>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="cust-notes">Notes</Label>
            <Textarea id="cust-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" rows={2} />
          </div>

          {/* QB Sync (internal) */}
          <div className="border-t pt-3 space-y-1">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">QuickBooks Sync</p>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span>QB Customer ID: {editCustomer?.qb_customer_id || "—"}</span>
              <Badge variant="outline" className="text-[10px]">
                {editCustomer?.qb_sync_status === "synced" ? "Synced" :
                 editCustomer?.qb_sync_status === "pending" ? "Pending" :
                 editCustomer?.qb_sync_status === "error" ? "Error" : "Not Synced"}
              </Badge>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "Saving…" : isEdit ? "Update Customer" : "Create Customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
